import { useCallback, useEffect, useMemo, useState } from "react";
import { aggregateUsage } from "./sessionData";

const emptyQueue = () => ({ entries: {}, pending: [], active: 0, disposed: false });

// Hydrate only rows in view, three workers at a time. A row reads the whole
// session (up to ROW_PAGE_LIMIT pages) because the list shows full estimates
// only; a session past the cap stays partial and the drawer finishes it.
export const ROW_PAGE_LIMIT = 30;
export function useSessionComparisons(api, clientSlug, refreshKey) {
  // Each scope owns its queue before child visibility effects run. Refresh cannot
  // read the previous scope's cache or clear a newly queued visible row afterward.
  const queue = useMemo(() => ({ ...emptyQueue(), api, clientSlug, refreshKey }), [api, clientSlug, refreshKey]);
  const [state, setState] = useState({ queue, entries: {} });
  useEffect(() => {
    queue.disposed = false;
    return () => { queue.disposed = true; };
  }, [queue]);

  const request = useCallback((sessionId) => {
    const current = queue;
    if (current.disposed || current.entries[sessionId]) return;
    const publish = (id, value) => {
      if (current.disposed) return;
      current.entries[id] = value;
      setState({ queue: current, entries: { ...current.entries } });
    };
    publish(sessionId, { status: "loading" });
    current.pending.push(sessionId);
    const drain = () => {
      while (!current.disposed && current.active < 3 && current.pending.length) {
        const id = current.pending.shift();
        current.active++;
        (async () => {
          const pages = [];
          const seen = new Set();
          let cursor, snapshot;
          try {
            for (let index = 0; index < ROW_PAGE_LIMIT; index++) {
              if (current.disposed) return;
              const page = await current.api.getAgentConversationUsage({ sessionId: id, cursor });
              if (current.disposed) return;
              if (page.clientSlug !== current.clientSlug || page.sessionId !== id) throw new Error("Workspace response mismatch");
              if (snapshot && page.coverage?.asOf !== snapshot) throw new Error("Snapshot changed");
              snapshot ||= page.coverage?.asOf;
              if (page.nextCursor != null && (page.nextCursor === cursor || seen.has(page.nextCursor))) throw new Error("Pagination did not advance");
              pages.push(page);
              if (cursor != null) seen.add(cursor);
              cursor = page.nextCursor;
              if (cursor == null) break;
            }
            const summary = aggregateUsage(pages);
            const complete = cursor == null && pages.at(-1)?.coverage?.complete && !summary.inconsistent && !pages.some((page) => page.coverage?.errors?.length);
            publish(id, { status: summary.inconsistent ? "unavailable" : complete ? "ready" : "partial", summary, asOf: snapshot });
          } catch {
            publish(id, { status: "unavailable" });
          } finally {
            current.active--;
            drain();
          }
        })();
      }
    };
    drain();
  }, [queue]);
  return { entries: state.queue === queue ? state.entries : {}, request };
}
